import {
  isUserMessage,
  useConversations,
  useConversationThread,
} from '@cascade/react/conversations';
import type { TAskPayload, TMessage } from '@cascade/react/conversations';
import { useI18n } from '@cascade/react/i18n';
import { useProjects } from '@cascade/react/projects';
import { Loading, Text, Upload } from '@cascade/ui-library';
import type { TChatInputControlButtons, TUploadRef } from '@cascade/ui-library';
import clsx from 'clsx';
import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { AgentIntro } from './agent-intro/index.ts';
import {
  buildFileFeedback,
  getPrompts,
  hasAnsweredAsk,
} from './AgentChat.helpers.ts';
import classes from './AgentChat.module.scss';
import { ReplyControls } from './reply-controls/index.ts';
import { useAttachments } from './use-attachments/index.ts';
import { ChatThread } from '#components/chat-thread/index.ts';
import { ExperienceCard } from '#components/experience-card/index.ts';
import { useNpsSurvey } from '#hooks/index.ts';

export const AgentChat = () => {
  const { translate, translateError } = useI18n();
  const { settings, isFeatureEnabled } = useProjects();
  const {
    currentConversationId,
    isLoadingMessages,
    messagesError,
    roomError,
    hasPendingAnswer,
    ask,
    removeRequest,
  } = useConversations();

  const { messages, followUpQuestions } = useConversationThread();

  const attachments = useAttachments();
  const { startSurvey } = useNpsSurvey();
  const uploadRef = useRef<TUploadRef>(null);

  const { reset: resetAttachments, discard: discardAttachments } = attachments;
  const attachLabel = translate('agent.attach');
  const error = messagesError ?? roomError;
  const errorMessage = error ? translateError(error) : undefined;
  const hasMessages = messages.length > 0;
  const hasFiles = attachments.files.length > 0;
  const hasAnswered = hasAnsweredAsk(messages);
  const isConversationFilesEnabled = isFeatureEnabled(
    'enable_conversation_files',
  );

  const hasFailedUpload = attachments.failures.length > 0;
  const isUnavailable = messagesError !== undefined;
  const isChatDisabled =
    isUnavailable || attachments.isUploading || hasFailedUpload;

  const isUploadDisabled = isUnavailable || hasPendingAnswer;
  const isEmpty = !hasMessages && !isLoadingMessages && !isUnavailable;
  const fileFeedback = buildFileFeedback(attachments.failures, translateError);

  const openFilePicker = () => uploadRef.current?.openFilePicker();

  const controlButtons: TChatInputControlButtons = [
    {
      iconOnly: true,
      size: 'sm',
      variant: 'secondary',
      iconType: 'paperclip',
      label: attachLabel,
      disabled: isUploadDisabled,
      onClick: openFilePicker,
    },
  ];

  const renderBeforeContent = (message: TMessage): ReactNode => {
    if (isUserMessage(message) || !message.detectedExperience) {
      return undefined;
    }

    return (
      <div className={classes['detected-experience']}>
        <Text type='body-2' content={translate('agent.experience.detected')} />
        <ExperienceCard experience={message.detectedExperience} />
      </div>
    );
  };

  const renderAfterContent = (message: TMessage): ReactNode => {
    if (isUserMessage(message) || message.status !== 'complete') {
      return undefined;
    }

    return (
      <ReplyControls
        messageId={message.id}
        text={message.message ?? ''}
        feedback={message.feedback}
        feedbackMessage={message.feedback_message}
      />
    );
  };

  const prompts = getPrompts(
    followUpQuestions,
    settings?.suggested_questions ?? [],
    hasMessages,
  );

  const sendToAgent = ({ files, ...payload }: TAskPayload) => {
    const isSendingStagedFiles = !files && hasFiles;

    const sending = ask({
      ...payload,
      files: isSendingStagedFiles ? attachments.files : files,
    });

    if (isSendingStagedFiles) {
      resetAttachments();
      uploadRef.current?.clearFiles();
    }

    return sending;
  };

  useEffect(() => {
    if (!hasAnswered) {
      return;
    }

    startSurvey();
  }, [hasAnswered, startSurvey]);

  useEffect(() => {
    discardAttachments();
    uploadRef.current?.clearFiles();
  }, [currentConversationId, discardAttachments]);

  return (
    <div className={clsx(classes['agent-chat'], { [classes.empty]: isEmpty })}>
      {errorMessage ? (
        <Text
          type='body-2'
          color='text-danger'
          role='alert'
          content={errorMessage}
        />
      ) : null}
      {isEmpty ? <AgentIntro /> : null}
      {isLoadingMessages ? (
        <Loading contain label={translate('pending.messages')} />
      ) : (
        <div className={classes.chat}>
          <ChatThread
            messages={messages}
            hasPendingAnswer={hasPendingAnswer}
            suggestions={prompts}
            isDisabled={isChatDisabled}
            controlButtons={
              isConversationFilesEnabled ? controlButtons : undefined
            }
            stagingArea={
              isConversationFilesEnabled ? (
                <Upload
                  ref={uploadRef}
                  label={attachLabel}
                  hasCustomTrigger
                  compact
                  multiple
                  disabled={isUploadDisabled}
                  loadingFiles={attachments.loadingKeys}
                  fileFeedback={fileFeedback}
                  onChange={attachments.sync}
                />
              ) : null
            }
            renderBeforeContent={renderBeforeContent}
            renderAfterContent={renderAfterContent}
            onAsk={sendToAgent}
            onRemoveRequest={removeRequest}
          />
        </div>
      )}
      <Text
        className={classes.disclaimer}
        type='caption'
        color='text-40'
        content={translate('agent.disclaimer')}
      />
    </div>
  );
};
